/**
 * JotRx Patient Card
 *
 * Minimal CTA button on the patient detail page.
 * Text and action adapt to the current JotRx onboarding state.
 *
 * When fully set up:
 * - If patient is connected to DoseSpot → opens patient-specific SSO
 * - If patient is NOT connected → opens the patient connect modal first
 */

import { MedicationOutlined } from '@mui/icons-material';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import { useJotRxEncounterAction } from '../hooks/useJotRxEncounterAction';
import { type JotRxSetupState } from '../hooks/useJotRxSetupState';

import { EPrescriptionPatientConnectModal } from './EPrescriptionPatientConnectModal';

interface PatientData {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface JotRxPatientCardProps {
  patientId: string;
  patientData?: PatientData;
  presentation?: 'button' | 'menu';
  /**
   * Scope the menu item to the `fully_setup` state only. When the practice /
   * clinician is fully set up we render the item for connected AND
   * not-connected patients (the label adapts), but suppress it for the
   * practice-level setup states (`practice_connected`, `registered`,
   * `idp_complete`) so partial-setup CTAs don't leak into the patient actions
   * menu. Used by Patient Page v3's Actions menu.
   */
  fullySetupOnly?: boolean;
}

function getButtonLabel(state: JotRxSetupState, isPatientConnected: boolean): string {
  switch (state) {
    case 'not_connected':
    case 'practice_connected':
      return 'Set Up E-Prescribing';
    case 'registered':
      return 'Complete ID Proofing to ePrescribe!';
    case 'idp_complete':
      return 'Setup TFA to ePrescribe!';
    case 'fully_setup':
      return isPatientConnected ? 'Send Prescription' : 'Set Up Patient for E-Rx';
  }
}

function shouldOpenSso(state: JotRxSetupState): boolean {
  return state === 'fully_setup';
}

export function JotRxPatientCard({
  patientId,
  patientData,
  presentation = 'button',
  fullySetupOnly = false,
}: JotRxPatientCardProps) {
  const {
    setupState,
    isVisible,
    isPatientConnected,
    isPatientStatusLoading,
    handleClick,
    prescribeHref,
    getPrescribeHref,
    showConnectModal,
    setShowConnectModal,
    handleConnectSuccess,
    connectModalPatientData,
    proxyPrescribers,
    proxyMenuAnchor,
    closeProxyMenu,
    selectProxyPrescriber,
  } = useJotRxEncounterAction({
    patientId,
    patientData,
    source: 'patient_detail',
  });

  if (!isVisible || (fullySetupOnly && setupState !== 'fully_setup')) return null;

  const needsPatientSetup =
    setupState === 'fully_setup' && !isPatientStatusLoading && !isPatientConnected;
  const buttonLabel =
    shouldOpenSso(setupState) && isPatientStatusLoading
      ? 'Checking E-Rx Status'
      : getButtonLabel(setupState, isPatientConnected);

  return (
    <>
      {presentation === 'menu' ? (
        <MenuItem
          data-patient-page-action="prescribe"
          component={prescribeHref ? 'a' : 'button'}
          href={prescribeHref}
          target={prescribeHref ? '_blank' : undefined}
          rel={prescribeHref ? 'noopener noreferrer' : undefined}
          onClick={handleClick}
          disabled={isPatientStatusLoading}
        >
          <ListItemIcon>
            <MedicationOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {needsPatientSetup ? 'Set Up Patient for E-Rx' : 'Prescribe medication'}
          </ListItemText>
        </MenuItem>
      ) : (
        <Button
          variant={needsPatientSetup ? 'outlined' : 'contained'}
          fullWidth
          color="secondary"
          component={prescribeHref ? 'a' : 'button'}
          href={prescribeHref}
          target={prescribeHref ? '_blank' : undefined}
          rel={prescribeHref ? 'noopener noreferrer' : undefined}
          onClick={handleClick}
          disabled={setupState === 'fully_setup' && isPatientStatusLoading}
          sx={{
            py: 1.25,
            fontWeight: 600,
            borderRadius: 1.5,
            textTransform: 'none',
            ...(needsPatientSetup ? { boxShadow: 0, borderWidth: 2 } : { boxShadow: 1 }),
          }}
        >
          {buttonLabel}
        </Button>
      )}

      <Menu anchorEl={proxyMenuAnchor} open={!!proxyMenuAnchor} onClose={closeProxyMenu}>
        {proxyPrescribers.map(p => (
          <MenuItem
            key={p.userId}
            component={getPrescribeHref(p.userId) ? 'a' : 'li'}
            href={getPrescribeHref(p.userId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => selectProxyPrescriber(p.userId)}
          >
            Prescribe on behalf of {p.name}
          </MenuItem>
        ))}
      </Menu>

      <EPrescriptionPatientConnectModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        patientId={patientId}
        patientData={connectModalPatientData}
        onSuccess={handleConnectSuccess}
      />
    </>
  );
}
